import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, Paper, Chip, IconButton,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, Tabs, Tab, Tooltip,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Check as ApplyIcon,
  Edit as EditIcon,
  VisibilityOff as DismissIcon,
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';

const fmt$ = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;

// 'SOXL' -> 'SOXL'; 'SPXW  261019C08010000' -> 'SPXW'
const underlyingOf = (symbol) => (symbol || '').trim().split(/\s+/)[0];

const STATUS_CHIP = {
  open: { color: 'warning', label: 'Open' },
  resolved: { color: 'success', label: 'Resolved' },
  dismissed: { color: 'default', label: 'Dismissed' },
  auto_resolved: { color: 'info', label: 'Auto-resolved' },
};

const COLUMNS = [
  { key: 'account_name', label: 'Account' },
  { key: 'underlying', label: 'Symbol' },
  { key: 'issue_type', label: 'Type' },
  { key: 'trade_type', label: 'Trade' },
  { key: 'trade_date', label: 'Trade Date' },
  { key: 'stored_pl', label: 'Stored P/L', numeric: true },
  { key: 'computed_pl', label: 'Computed', numeric: true },
  { key: 'delta', label: 'Δ', numeric: true },
  { key: 'status', label: 'Status' },
  { key: 'first_seen_at', label: 'First Seen' },
];

const sortValue = (i, key) => {
  switch (key) {
    case 'account_name': return i.account_name || '';
    case 'underlying': return underlyingOf(i.symbol);
    case 'delta': return (i.computed_pl ?? 0) - (i.stored_pl ?? 0);
    default: return i[key] ?? '';
  }
};

// Closed-row P/L drift tracker — rows where stored P/L disagrees with what
// reconciliation recomputes. Edit flow mirrors the closed-positions editor:
// cost / proceeds / realized P/L / closing date.
const ReconIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState(0); // 0=open 1=all
  const [edit, setEdit] = useState(null); // { issue, cost, proceeds, realizedPL, closingDate }
  const [busy, setBusy] = useState(false);
  const [sortKey, setSortKey] = useState('trade_date');
  const [sortDir, setSortDir] = useState('desc');
  const [accountFilter, setAccountFilter] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest('/api/recon/issues');
      if (res.data?.success) setIssues(res.data.issues || []);
      else setError('Failed to load reconciliation issues');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (issue, body) => {
    setBusy(true);
    try {
      const res = await apiRequest(`/api/recon/issues/${issue.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      if (res.data?.success) {
        setNotice(`Issue ${issue.id} ${body.action === 'dismiss' ? 'dismissed' : 'resolved'}`);
        await load();
        return true;
      }
      setError(res.data?.error || 'Action failed');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
    return false;
  };

  const openEdit = (issue) => {
    const t = issue.trade || {};
    setEdit({
      issue,
      cost: t.cost ?? '',
      proceeds: t.proceeds ?? '',
      realizedPL: t.realized_pl ?? '',
      closingDate: (t.closing_date || '').slice(0, 10),
    });
  };

  const saveEdit = async () => {
    const ok = await act(edit.issue, {
      action: 'edit',
      cost: edit.cost === '' ? undefined : parseFloat(edit.cost),
      proceeds: edit.proceeds === '' ? undefined : parseFloat(edit.proceeds),
      realizedPL: edit.realizedPL === '' ? undefined : parseFloat(edit.realizedPL),
      closingDate: edit.closingDate || undefined,
    });
    if (ok) setEdit(null);
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const accounts = useMemo(
    () => [...new Set(issues.map(i => i.account_name).filter(Boolean))].sort(),
    [issues]
  );

  const tradeDates = useMemo(
    () => [...new Set(issues.map(i => i.trade_date).filter(Boolean))].sort().reverse(),
    [issues]
  );

  // Zero/empty cost basis on the underlying trade is the usual drift source —
  // those rows get shaded so they stand out for editing.
  const zeroCost = (i) => !i.trade || !parseFloat(i.trade.cost);

  const openCount = issues.filter(i => i.status === 'open').length;

  const shown = useMemo(() => {
    const rows = issues.filter(i =>
      (tab === 0 ? i.status === 'open' : true) &&
      (!accountFilter || i.account_name === accountFilter) &&
      (!symbolFilter || underlyingOf(i.symbol).toUpperCase().includes(symbolFilter.toUpperCase())) &&
      (!dateFilter || i.trade_date === dateFilter)
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    return rows.slice().sort((a, b) => {
      const va = sortValue(a, sortKey), vb = sortValue(b, sortKey);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [issues, tab, accountFilter, symbolFilter, dateFilter, sortKey, sortDir]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Closed trades whose stored P/L differs from what reconciliation recomputes.
        Alerts only fire for new issues — resolve them here by applying the computed
        value, editing the trade, or dismissing.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`Open (${openCount})`} />
          <Tab label="All" />
        </Tabs>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Account</InputLabel>
          <Select value={accountFilter} label="Account" onChange={e => setAccountFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {accounts.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" label="Symbol" placeholder="e.g. SOXL, SPXW"
          value={symbolFilter} onChange={e => setSymbolFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Trade Date</InputLabel>
          <Select value={dateFilter} label="Trade Date" onChange={e => setDateFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {tradeDates.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box> : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMNS.map(c => (
                      <TableCell key={c.key} align={c.numeric ? 'right' : 'left'}
                        sortDirection={sortKey === c.key ? sortDir : false}>
                        <TableSortLabel
                          active={sortKey === c.key}
                          direction={sortKey === c.key ? sortDir : 'asc'}
                          onClick={() => toggleSort(c.key)}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map(i => {
                    const delta = (i.computed_pl ?? 0) - (i.stored_pl ?? 0);
                    const s = STATUS_CHIP[i.status] || { color: 'default', label: i.status };
                    return (
                      <TableRow key={i.id} hover
                        sx={zeroCost(i) ? { bgcolor: 'rgba(255, 152, 0, 0.12)' } : undefined}
                        title={zeroCost(i) ? 'Underlying trade has no cost basis — likely source of the drift' : undefined}>
                        <TableCell>{i.account_name || i.account_id}</TableCell>
                        <TableCell>{i.symbol}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined"
                            label={i.issue_type === 'manual_preserved' ? 'Manual' : 'Drift'}
                            color={i.issue_type === 'manual_preserved' ? 'info' : 'warning'} />
                        </TableCell>
                        <TableCell>{i.trade_type}</TableCell>
                        <TableCell>{i.trade_date}</TableCell>
                        <TableCell align="right">{fmt$(i.stored_pl)}</TableCell>
                        <TableCell align="right">{fmt$(i.computed_pl)}</TableCell>
                        <TableCell align="right" sx={{ color: delta < 0 ? 'error.main' : 'success.main' }}>
                          {fmt$(delta)}
                        </TableCell>
                        <TableCell><Chip size="small" color={s.color} label={s.label} /></TableCell>
                        <TableCell>{(i.first_seen_at || '').slice(0, 10)}</TableCell>
                        <TableCell align="right">
                          {i.status === 'open' && (
                            <>
                              <Tooltip title="Apply computed P/L">
                                <span><IconButton size="small" color="success" disabled={busy}
                                  onClick={() => act(i, { action: 'apply' })}><ApplyIcon fontSize="small" /></IconButton></span>
                              </Tooltip>
                              <Tooltip title="Edit trade (cost / proceeds / P/L)">
                                <span><IconButton size="small" color="primary" disabled={busy}
                                  onClick={() => openEdit(i)}><EditIcon fontSize="small" /></IconButton></span>
                              </Tooltip>
                              <Tooltip title="Dismiss — keep stored value">
                                <span><IconButton size="small" disabled={busy}
                                  onClick={() => act(i, { action: 'dismiss' })}><DismissIcon fontSize="small" /></IconButton></span>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!shown.length && (
                    <TableRow><TableCell colSpan={11} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {tab === 0 ? 'No open issues — reconciliation is clean.' : 'No issues recorded.'}
                      </Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog — mirrors the closed-positions economics editor */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Trade — {edit?.issue?.symbol}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {edit?.issue?.account_name} · {edit?.issue?.trade_type} {edit?.issue?.trade_date}
            {' '}· computed net P/L {fmt$(edit?.issue?.computed_pl)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Cost" type="number" value={edit?.cost ?? ''} fullWidth
              onChange={e => setEdit(p => ({ ...p, cost: e.target.value }))} />
            <TextField label="Proceeds" type="number" value={edit?.proceeds ?? ''} fullWidth
              onChange={e => setEdit(p => ({ ...p, proceeds: e.target.value }))} />
            <TextField label="Realized P/L" type="number" value={edit?.realizedPL ?? ''} fullWidth
              onChange={e => setEdit(p => ({ ...p, realizedPL: e.target.value }))}
              helperText="Net P/L recomputes as realized − commission − fees" />
            <TextField label="Closing date" type="date" value={edit?.closingDate ?? ''} fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={e => setEdit(p => ({ ...p, closingDate: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy}>
            Save & Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReconIssues;
